import { HttpClient } from './services';
import { gatewayUrl } from '@constants/settings';
import {
    ApiEndpoint,
    ApiGroup,
    CatalogResponse,
    CatalogStats,
    CreateApiGroupRequest,
    CreateServicePrincipalRequest,
    ServicePrincipal,
} from '@pages/api-portal/types';

function catalogBaseUrl() {
    return `${gatewayUrl}/usermanagement/api/v1/catalog`;
}

function servicePrincipalsBaseUrl() {
    return `${gatewayUrl}/usermanagement/api/v1/service-principals`;
}

/*
 * Fetches all API groups. Optional/degrading endpoint — returns an empty list on failure
 * instead of throwing, since the catalog and portal overview can render without it.
 */
async function fetchApiGroups(): Promise<CatalogResponse<ApiGroup[]>> {
    try {
        const res = await HttpClient.GetPromise(`${catalogBaseUrl()}/groups`);
        return res.data;
    } catch (error) {
        return { data: [], meta: { total: 0, page: 1, limit: 0 } };
    }
}

/*
 * Fetches a single API group by id. Returns a null-data response on failure.
 */
async function fetchApiGroupById(groupId: string): Promise<CatalogResponse<ApiGroup | null>> {
    try {
        const res = await HttpClient.GetPromise(`${catalogBaseUrl()}/groups/${groupId}`);
        return res.data;
    } catch (error) {
        return { data: null, meta: { total: 0, page: 1, limit: 0 } };
    }
}

/*
 * Fetches every API endpoint. Optional/degrading endpoint — returns an empty list on failure.
 */
async function fetchAllEndpoints(): Promise<CatalogResponse<ApiEndpoint[]>> {
    try {
        const res = await HttpClient.GetPromise(`${catalogBaseUrl()}/endpoints`);
        return res.data;
    } catch (error) {
        return { data: [], meta: { total: 0, page: 1, limit: 0 } };
    }
}

/*
 * Fetches a single API endpoint by id. Returns a null-data response on failure.
 */
async function fetchEndpointById(endpointId: string): Promise<CatalogResponse<ApiEndpoint | null>> {
    try {
        const res = await HttpClient.GetPromise(`${catalogBaseUrl()}/endpoints/${endpointId}`);
        return res.data;
    } catch (error) {
        return { data: null, meta: { total: 0, page: 1, limit: 0 } };
    }
}

/*
 * Fetches catalog-wide stats for the Portal overview. Optional/degrading endpoint —
 * returns an all-zero stats object on failure.
 */
async function fetchCatalogStats(): Promise<CatalogResponse<CatalogStats>> {
    try {
        const res = await HttpClient.GetPromise(`${catalogBaseUrl()}/stats`);
        return res.data;
    } catch (error) {
        return {
            data: {
                totalEndpoints: 0,
                totalCategories: 0,
                stableCount: 0,
                betaCount: 0,
                deprecatedCount: 0,
                methodCounts: {},
            },
            meta: { total: 0, page: 1, limit: 0 },
        };
    }
}

/*
 * Creates an API group. Mutation — propagates errors so the caller can show a toast.
 */
async function createApiGroup(group: CreateApiGroupRequest): Promise<CatalogResponse<ApiGroup>> {
    const res = await HttpClient.PostPromise(`${catalogBaseUrl()}/groups`, group);
    return res.data;
}

/*
 * Updates an API group (groupKey is immutable and never sent here).
 */
async function updateApiGroup(
    groupId: string,
    group: Partial<Omit<ApiGroup, 'id' | 'endpoints' | 'groupKey'>>,
): Promise<CatalogResponse<ApiGroup>> {
    const res = await HttpClient.PutPromise(`${catalogBaseUrl()}/groups/${groupId}`, group);
    return res.data;
}

/*
 * Deletes an API group.
 */
async function deleteApiGroup(groupId: string): Promise<void> {
    await HttpClient.DeletePromise(`${catalogBaseUrl()}/groups/${groupId}`);
}

/*
 * Creates an API endpoint.
 */
async function createApiEndpoint(endpoint: Omit<ApiEndpoint, 'id'>): Promise<CatalogResponse<ApiEndpoint>> {
    const res = await HttpClient.PostPromise(`${catalogBaseUrl()}/endpoints`, endpoint);
    return res.data;
}

/*
 * Updates an API endpoint.
 */
async function updateApiEndpoint(
    endpointId: string,
    endpoint: Partial<Omit<ApiEndpoint, 'id'>>,
): Promise<CatalogResponse<ApiEndpoint>> {
    const res = await HttpClient.PutPromise(`${catalogBaseUrl()}/endpoints/${endpointId}`, endpoint);
    return res.data;
}

/*
 * Deletes an API endpoint.
 */
async function deleteApiEndpoint(endpointId: string): Promise<void> {
    await HttpClient.DeletePromise(`${catalogBaseUrl()}/endpoints/${endpointId}`);
}

/*
 * Fetches all service principals.
 */
async function fetchServicePrincipals(): Promise<ServicePrincipal[]> {
    const res = await HttpClient.GetPromise(servicePrincipalsBaseUrl());
    return res.data.data;
}

/*
 * Fetches a single service principal by id. Returns null on failure (non-throwing lookup).
 */
async function fetchServicePrincipalById(id: number): Promise<ServicePrincipal | null> {
    try {
        const res = await HttpClient.GetPromise(`${servicePrincipalsBaseUrl()}/${id}`);
        return res.data.data;
    } catch (error) {
        return null;
    }
}

/*
 * Creates a service principal. This also provisions a real DataStudio user
 * ({name}@deepiq.com, ADMIN role) on the backend — the response's `password` field is the
 * only time that credential is ever returned, so callers must surface it immediately.
 */
async function createServicePrincipal(request: CreateServicePrincipalRequest): Promise<ServicePrincipal> {
    const res = await HttpClient.PostPromise(servicePrincipalsBaseUrl(), {
        name: request.name,
        description: request.description,
        status: request.status || 'active',
    });
    return res.data.data;
}

/*
 * Deletes a service principal. The backend cascades this to the corresponding DataStudio user.
 */
async function deleteServicePrincipal(id: number): Promise<void> {
    await HttpClient.DeletePromise(`${servicePrincipalsBaseUrl()}/${id}`);
}

export const ApimHandler = {
    fetchApiGroups,
    fetchApiGroupById,
    fetchAllEndpoints,
    fetchEndpointById,
    fetchCatalogStats,
    createApiGroup,
    updateApiGroup,
    deleteApiGroup,
    createApiEndpoint,
    updateApiEndpoint,
    deleteApiEndpoint,
    fetchServicePrincipals,
    fetchServicePrincipalById,
    createServicePrincipal,
    deleteServicePrincipal,
};
